import { connect } from 'react-redux';
import MojitoCore from 'mojito/core';
import Image from 'presentation/components/image/index.js';
import SelectableButton from 'presentation/components/selectable-button/index.js';
import FlexPane from 'presentation/components/flex-pane/index.js';
import Text from 'presentation/components/text/index.js';

const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const intentActions = MojitoCore.Intents.actions;
const classUtils = MojitoCore.Base.classUtils;

class LinkMenu extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.handleClick = this.handleClick.bind(this);
    }

    handleClick(event, item) {
        const { dispatch } = this.props;
        const { intent, analyticsData } = item;
        if (analyticsData?.type) {
            this.emitAnalytics(analyticsData.type, analyticsData.linkName);
        }
        if (intent?.type) {
            dispatch(intentActions.publishIntent(intent.type, intent.data));
        }
        this.props.onItemClick(item);
    }

    renderIcon(iconSrc, iconFill, altText) {
        if (!iconSrc) {
            return null;
        }

        return (
            <FlexPane config={this.config.iconContainer}>
                <Image config={this.config.icon} src={iconSrc} fill={iconFill} alt={altText} />
            </FlexPane>
        );
    }

    renderHeaderIcon(headerIcon) {
        const { showCompactLayout, regularItem, compactItem } = this.config;
        return this.renderIcon(
            headerIcon,
            showCompactLayout ? compactItem.color : regularItem.color
        );
    }

    renderHeader() {
        const { header = {} } = this.props;
        if (!this.config.showHeader || !header.label) {
            return null;
        }

        return (
            <FlexPane config={this.config.headerContainer}>
                {this.renderHeaderIcon(header.icon)}
                <Text class={'ta-LinkMenuHeader'} config={this.config.header}>
                    {header.label}
                </Text>
            </FlexPane>
        );
    }

    renderSubHeader(header, key) {
        if (this.config.showCompactLayout) {
            return null;
        }

        return (
            <FlexPane config={this.config.subHeaderContainer} key={key}>
                <Text class={'ta-LinkMenuSubHeader'} config={this.config.subHeader}>
                    {header.label}
                </Text>
            </FlexPane>
        );
    }

    renderItemIcon(item, selected) {
        const { showCompactLayout, regularItem, compactItem } = this.config;
        const layout = showCompactLayout ? compactItem : regularItem;
        const fillColor = selected
            ? layout.selected.style.base.color
            : layout.unselected.style.base.color;

        return item.iconRenderer
            ? item.iconRenderer()
            : this.renderIcon(item.icon, fillColor, item.altText);
    }

    renderItemLabel(item) {
        if (this.config.showCompactLayout) {
            return <Text config={this.config.compactLabel}>{item.label}</Text>;
        }

        return <Text config={this.config.regularLabel}>{item.label}</Text>;
    }

    renderExtraItemDetail() {
        const { showExtraItemDetails, extraItemDetails } = this.config;
        if (!showExtraItemDetails) {
            return null;
        }
        return <div style={extraItemDetails.style}>{extraItemDetails.text}</div>;
    }

    renderItem(item) {
        const { showCompactLayout, regularItem, compactItem } = this.config;
        const className = classUtils.classNames(
            'ta-LinkMenuItem',
            item.className,
            `ta-${item.intent ? item.intent.type : 'noIntent'}`
        );
        const { data: intentData = {} } = item.intent || {};
        const hrefLink = item.hrefLink || intentData.url;
        const shouldRenderIcons = this.props.shouldRenderIcons;
        return (
            <SelectableButton
                class={className}
                onClick={this.handleClick}
                onClickData={item}
                config={showCompactLayout ? compactItem : regularItem}
                key={`${item.label}_${item.isSelected}`}
                hrefLink={hrefLink}
                selected={item.isSelected}
            >
                {shouldRenderIcons && this.renderItemIcon(item, item.isSelected)}
                {this.renderItemLabel(item)}
                {this.renderExtraItemDetail()}
            </SelectableButton>
        );
    }

    renderItems() {
        const { items = [] } = this.props;
        return items.reduce((acc, currentItem, i) => {
            if (currentItem.items) {
                if (currentItem.header) {
                    acc.push(this.renderSubHeader(currentItem.header, i));
                }

                currentItem.items.forEach(item => {
                    acc.push(this.renderItem(item));
                });
            } else {
                acc.push(this.renderItem(currentItem));
            }
            return acc;
        }, []);
    }

    hasEmptyData() {
        const { items = [], header = {} } = this.props;
        return items.length === 0 && !header.label;
    }

    render() {
        if (this.hasEmptyData()) {
            return null;
        }

        const containerConfig = this.config.showCompactLayout
            ? this.config.compactContainer
            : this.config.regularContainer;

        return (
            <FlexPane
                class={classUtils.classNames('ta-LinkMenu', this.props.class)}
                config={containerConfig}
            >
                {this.renderHeader()}
                {this.renderItems()}
            </FlexPane>
        );
    }
}

export default connect()(LinkMenu);
